import { Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { IEntity } from '../../../../primitives';
import { IPaginationParameters, IPaginationResponse } from '../../../../data-access/pagination/interfaces';
import { PaginationResponseModel } from '../../../../data-access/pagination/models';
import { SharedTableComponent } from '../../../../../../trash/shared/components/shared-table/shared-table.component';
import { IPaginationResponseResolver } from '../../../../data-access/pagination/types';
import { ISharedTableConfig, ITableConfig, TableManager } from '../../configs';
import { CommonModule } from '@angular/common';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { SkeletonModule } from 'primeng/skeleton';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { tap } from 'rxjs';
import {
  ActionConfigManager,
  ActionsManager,
  ActionTypeEnum,
  IActionConfig,
  IActionExecutedEvent,
  TableActions
} from '../../table-actions';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormlyFieldConfig } from '@ngx-formly/core';

@Component({
  standalone: true,
  selector: 'app-paginated-table',
  templateUrl: './paginated-table.component.html',
  styleUrls: ['./paginated-table.component.scss'],
  imports: [
    SharedTableComponent,
    CommonModule,
    SkeletonModule,
    CommonModule,
    DynamicDialogModule,
    RouterModule,
    ButtonModule,
    MenuModule,
    TableActions,
    TranslateModule
  ],
  providers: [DialogService]
})
export class PaginatedTableComponent<
  TEntity extends IEntity<string>,
  TPaginationParameters extends IPaginationParameters
> implements OnInit
{
  private translate = inject(TranslateService);
  handelRenderingDataMessage = signal<string>(this.translate.instant('GENERAL.LOADING'));
  createButtonHeader = input<string>('Create New Item');

  paginationResponseResolver = input.required<IPaginationResponseResolver<TEntity, TPaginationParameters>>();
  defaultPaginationParameters = input.required<TPaginationParameters>();

  tableConfig = input.required<ITableConfig>();
  sharedtableConfig = computed<ISharedTableConfig<TEntity>>(() => this.tableConfigResolver()(this.response()?.items));
  tableManager = computed(() => TableManager.create<TEntity>(this.tableConfig()));
  tableConfigResolver = computed(() => this.tableManager().getTableConfigResolver(this.actionsManager()));

  supportedActions = input.required<IActionConfig<TEntity>[]>();
  actionsManager = computed(() => ActionsManager.create(this.supportedActions()));
  actionExecuted = output<IActionExecutedEvent<TEntity>>();
  selfActionExecuted = output<IActionExecutedEvent<TEntity>>();

  createActionManger = computed<ActionConfigManager<TEntity> | undefined>(() =>
    this.actionsManager().getConfig(ActionTypeEnum.CREATE)
  );

  // Filter
  showFilterBtn = input<boolean>(false);
  filterOptions = input<FormlyFieldConfig[]>([]);
  sidebarVisible = signal<boolean>(false);
  sidebarVisibleChange = output<boolean>();
  applyFilter = output<any>();
  clearFilter = output<void>();
  filterValues = signal<any>({});

  initialPageSize = input<number>(10);
  isLoading = signal<boolean>(false);
  currentPage = signal<number>(1);
  pageSize = signal<number>(this.initialPageSize());
  searchKeyword = signal<string>('');
  response = signal<PaginationResponseModel<TEntity> | undefined>(undefined);

  paginationParams = computed(() => ({
    ...this.defaultPaginationParameters(),
    ...this.filterValues(),
    page: this.currentPage(),
    pageSize: this.pageSize(),
    searchKeyword: this.searchKeyword()
  }));

  pageChange = output<number>();
  pageSizeChange = output<number>();
  search = output<string>();

  ngOnInit(): void {
    this.loadData();
    this.selfActionExecuted.subscribe((event) => this.onActionExecuted(event));
  }

  onPageChange(page: number, forceLoad: boolean = true): void {
    this.pageChange.emit(page);
    this.currentPage.set(page);

    if (forceLoad) {
      this.loadData();
    }
  }

  onPageSizeChange(pageSize: number, forceLoad: boolean = true): void {
    this.pageSizeChange.emit(pageSize);
    this.pageSize.set(pageSize);
    this.onPageChange(1, forceLoad);
  }

  onSearch(searchKeyword: string, forceLoad: boolean = true): void {
    this.search.emit(searchKeyword);
    this.searchKeyword.set(searchKeyword);

    if (forceLoad) {
      this.loadData();
    }
  }

  createElement() {
    this.actionsManager().execute(ActionTypeEnum.CREATE, undefined, () => {
      this.resetParams();
      this.loadData();
    });
    // const ref: DynamicDialogRef = this.dialogManager().openDialog(this.dialogService, ActionTypeEnum.CREATE, undefined);
    // ref?.onClose?.subscribe((item?: TEntity) => {
    //   if (item) {
    //     this.resetParams();
    //     this.loadData();
    //   }
    // });
  }

  onUpdated() {
    this.resetParams();
    this.loadData();
  }

  onActionExecuted(event: unknown) {
    let actionExecutedEvent = event as IActionExecutedEvent<TEntity>;

    if (actionExecutedEvent.config.type === ActionTypeEnum.VIEW) {
      return;
    }

    this.actionExecuted.emit(actionExecutedEvent);

    this.resetParams();
    this.loadData();
  }

  private loadData(): void {
    this.isLoading.set(true);
    this.paginationResponseResolver()
      .get(this.paginationParams())
      .pipe(tap((response) => this.processListResponse(response)))
      .subscribe();
  }

  private processListResponse(response: IPaginationResponse<TEntity>): void {
    this.isLoading.set(false);
    if (!response || response.items.length === 0) {
      this.handelRenderingDataMessage.set('GENERAL.NO_DATA_FOUND');
      this.response.set(PaginationResponseModel.create(response));
      return;
    }

    this.response.set(PaginationResponseModel.create(response));
  }

  private resetParams() {
    this.onPageChange(1, false);
    this.onPageSizeChange(10, false);
    this.onSearch('', false);
    this.filterValues.set({});
  }

  // Filter
  onSidebarVisibleChange(visible: boolean): void {
    this.sidebarVisible.set(visible);
    this.sidebarVisibleChange.emit(visible);
  }

  onApplyFilter(filters: any): void {
    this.filterValues.set(filters);
    this.applyFilter.emit(filters);
    this.onPageChange(1, true);
  }

  onClearFilter(): void {
    this.filterValues.set({});
    this.clearFilter.emit();
    this.onPageChange(1, true);
  }
}
